import { createContext, useContext, type ReactNode } from "react";
import type { ToolCategoryName } from "../hooks/toolActivity";

/**
 * Labels for the chat tool-call indicator.
 *
 * The package itself stays domain-agnostic — it has no idea what
 * "uploads", "stock_images", or "uploads.from_url" mean. Consumers
 * supply the user-facing strings via this context.
 *
 * Shape mirrors the `getToolLabelMap()` helper in
 * `@launch10/shared/mcp/registry` (the launch10 consumer's source of
 * truth). The Rails side declares labels on the `mcp_resource` and
 * `mcp_callable` macros; the exporter writes them into the registry;
 * the consumer reads them and passes them in here.
 *
 * - **`resources`**: resource name → label. Used by `search`,
 *   `describe`, and `list_resources` indicators. Example:
 *   `{ uploads: "your library", stock_images: "stock photos" }`.
 * - **`operations`**: `<resource>.<action>` → label. Used by the
 *   `call` indicator. Example:
 *   `{ "uploads.from_url": "Saving image to your library" }`.
 * - **`tools`**: bespoke tool name → label. For tools that aren't on
 *   the MCP surface (e.g. `save_answers`, `change_color_scheme`).
 *   Example: `{ save_answers: "Saving your answers" }`.
 */
export interface ToolLabels {
  resources?: Record<string, string>;
  operations?: Record<string, string>;
  tools?: Record<string, string>;
  /**
   * Tool name → display category for the ToolActivity card's aggregate
   * tallies ("Read 3 · Edited 2 · Searched 4"). Merged over the
   * package's `DEFAULT_TOOL_CATEGORIES` (deepagents/MCP universals);
   * unmapped tools land in the generic `work` bucket — visible by
   * default, never silently dropped.
   */
  categories?: Record<string, ToolCategoryName>;
  /**
   * Subagent type → friendly name for `delegate`-category rows (the
   * deepagents `task` tool). Example: `{ coder: "Site builder" }` turns
   * a dispatch into "Site builder: Update the navigation…". Unmapped
   * types humanize.
   */
  agents?: Record<string, string>;
  /**
   * Tool names whose call indicators are suppressed entirely — no chip, even
   * while running. For tools whose output renders its own UI (e.g.
   * `create_or_edit_artifact`, which surfaces an artifact card), a "ran a tool"
   * indicator is redundant noise. Consumer-supplied so the package stays
   * domain-agnostic.
   */
  hidden?: readonly string[];
}

const EMPTY: ToolLabels = {};

const ToolLabelsContext = createContext<ToolLabels>(EMPTY);

/**
 * Hook for ToolCallIndicator (and any future consumer). Returns the
 * labels the closest provider supplied, or empty maps if no provider
 * is mounted — falling back to humanized tool names is the indicator's
 * concern, not the context's.
 */
export function useToolLabels(): ToolLabels {
  return useContext(ToolLabelsContext);
}

export interface ToolLabelsProviderProps {
  value: ToolLabels;
  children: ReactNode;
}

/**
 * Wrap the chat tree once near the root and pass labels in.
 *
 *   <ToolLabelsProvider value={getToolLabelMap()}>
 *     <Chat ... />
 *   </ToolLabelsProvider>
 *
 * Re-providing further down the tree overrides — useful for testing or
 * for surfaces that want different copy than the global default.
 */
export function ToolLabelsProvider({ value, children }: ToolLabelsProviderProps) {
  return (
    <ToolLabelsContext.Provider value={value}>
      {children}
    </ToolLabelsContext.Provider>
  );
}
